import { memo } from 'react';
import { useTranslation } from 'react-i18next';

import type { ArticleDetail, LocalizedContent } from '@/types/newsService';
import { withAlpha } from '@/lib/withAlpha';

interface Props {
  article: ArticleDetail;
  localized: LocalizedContent;
}

/**
 * Article hero — image with a dark gradient overlay, content-type badge,
 * primary tag chip, and title.
 *
 * Ported from mobile-app/src/components/news/ArticleHeader.tsx. The linear
 * gradient is implemented as a CSS overlay div instead of a separate
 * `LinearGradient` component.
 */
export const ArticleHeader = memo(function ArticleHeader({ article, localized }: Props) {
  const { t } = useTranslation('news');
  const tagBg = article.primaryTag?.color ? withAlpha(article.primaryTag.color, 0.13) : undefined;
  const tagFg = article.primaryTag?.color;

  return (
    <div>
      {/* La copertina è una CARD, non un fondale.
          Prima usciva a filo di schermo con una sfumatura scura in basso, e
          quella sfumatura serviva a un solo scopo: rendere leggibili titolo ed
          etichette che le stavano sopra. Adesso il testo sta sotto, quindi la
          sfumatura scuriva un quarto di fotografia per proteggere un testo che
          lì non c'è più — mangiava l'immagine senza rendere nulla.
          Rientrata e arrotondata, la foto entra nella stessa famiglia delle
          card del resto dell'app. Il `px-5` è lo stesso del testo qui sotto:
          la colonna di lettura deve essere UNA, e un'immagine a filo di
          schermo la spezzava in due larghezze diverse. */}
      {article.heroImage && (
        // `pt-3`: la copertina è la prima cosa della schermata e senza respiro
        // sopra si legge come tagliata dal bordo. `pt-1` bastava quando sopra
        // c'era la chrome a fare da stacco; tolta quella, non c'è più niente.
        <div className="px-5 pt-3">
          <img
            src={article.heroImage.url}
            alt={article.heroImage.alt}
            className="card-edge block h-[240px] w-full rounded-2xl object-cover"
          />
        </div>
      )}

      {/* Margine normale e non più risalita negativa: senza la sfumatura non c'è
          più niente su cui appoggiarsi, e un'etichetta a cavallo di un bordo
          arrotondato si legge come un ritaglio sbagliato. */}
      <div className="mt-4 px-5">
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <span className="rounded-lg bg-white/10 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.5px] text-fg-muted">
            {t(`contentType.${article.contentType}`, { defaultValue: article.contentType })}
          </span>
          {article.primaryTag && (
            <span
              className="rounded-lg px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.5px]"
              style={
                tagBg
                  ? { backgroundColor: tagBg, color: tagFg }
                  : {
                      backgroundColor: 'color-mix(in srgb, var(--color-brand) 12%, transparent)',
                      color: 'var(--color-brand)',
                    }
              }
            >
              {article.primaryTag.name}
            </span>
          )}
        </div>

        <h1 className="mb-2 text-2xl font-bold leading-8 text-fg">{localized.title}</h1>
      </div>
    </div>
  );
});
